import { useUpdateAdmin } from "@entities/admin";
import { useAuth } from "@entities/auth";
import { useGetBeUser } from "@entities/auth/lib/hooks/useGetUser";
import { countryCodeOptions } from "@shared/lib/constants";
import { FirebaseService, ToastService } from "@shared/lib/services";
import {
  Avatar,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Tooltip,
  Typography,
  Upload,
} from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Select as AntdSelect, Flex, Form, Spin, UploadProps } from "antd";
import { HelpCircle, User } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

const { Text } = Typography;

const TIMER = {
  mins: 1,
  secs: 0,
};

const MockCheckEmail: React.FC<{ email?: string; onCancel: () => void }> = ({
  email,
  onCancel,
}) => {
  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "12px" }}>
        Check your email
      </h2>
      <p style={{ color: "#697386", fontSize: "14px", marginBottom: "16px" }}>
        We've sent you an email at{" "}
        <span style={{ fontWeight: 600, color: "#1a1f36" }}>{email}</span>{" "}
        containing further instructions for resetting your password.
      </p>
      <p style={{ color: "#697386", fontSize: "14px", marginBottom: "20px" }}>
        If you haven't received an email in 5 minutes, check your spam or junk
        folder.
      </p>
      <Button type="primary" onClick={onCancel}>
        Close
      </Button>
    </div>
  );
};

export default function ProfileTab() {
  const { beUser, isLoading } = useGetBeUser();
    const { isUpdated, isUpdating, update } = useUpdateAdmin();
    const {resetPassword} = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  useEffect(() => {
    if (beUser?.profilePicUrl) {
      setProfilePhoto(beUser.profilePicUrl);
    }
  }, [beUser]);

  const [isResetting, setIsResetting] = useState(false);

  const [triggerCheckEmail, setTriggerCheckEmail] = useState<boolean>(false);
  const [disableTimer, setDisableTimer] = useState<{
    mins: number;
    secs: number;
  }>({
    mins: 0,
    secs: 0,
  });
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (isUpdated) {
      ToastService.success("Profile edited successfully.");
      setIsEditing(false);
    }
  }, [isUpdated]);

  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "profile-pictures",
        file
      );
      setProfilePhoto(uploadedUrl);
      ToastService.success("Image uploaded successfully.");
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleDeleteImage = () => {
    setProfilePhoto("");
    ToastService.success("Profile picture removed successfully.");
  };

  const handleProfileSave = (values: any) => {
    update({
      data: {
        countryCode: values.countryCode,
        firstName: values.name.split(" ")[0] || "",
        lastName: values.name.split(" ")[1] || "",
        phoneNumber: values.phoneNumber,
        role: values.jobRole,
        profilePicUrl: profilePhoto || "",
      },
      userId: beUser?._id || ""
    });
  };

  const passwordReset = async () => {
    setIsResetting(true);
    // Simulate password reset request
    try{

    await resetPassword(beUser?.emailId || "");
    setDisableTimer({
      mins: TIMER.mins,
      secs: TIMER.secs,
    });
    let interval: any;
    interval = setInterval(() => {
      setDisableTimer((prev) => {
        if (prev.mins === 0 && prev.secs === 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return prev;
        } else if (prev.secs === 0 && prev.mins > 0) {
          return {
            mins: prev.mins - 1,
            secs: 59,
          };
        }
        return {
          mins: prev.mins,
          secs: prev.secs - 1,
        };
      });
    }, 1000);
    intervalRef.current = interval;
    setTriggerCheckEmail(true);
    }
    catch (err) {
      console.log(err)
    }
    finally {
    setIsResetting(false);
    }
  };

  const uploadProps: UploadProps = {
    name: "avatar",
    showUploadList: false,
    accept: "image/jpeg,image/jpg,image/png",
    beforeUpload: (file) => {
      const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(
        file.type
      );
      if (!isImage) {
        ToastService.error(
          "You can only upload JPG, JPEG, or PNG files!"
        );
        return false;
      }
      handleImageUpload(file);
      return false; // Prevent auto-upload post request
    },
  };

  if (isLoading) {
    return <Spin spinning />;
  }


  const permissionGroups = beUser?.permissionGroups?.map((val) => val.name).join(", ");
  return (
    <Card
      title="Basic Information"
      extra={
        isEditing ? (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={() => {
              setIsEditing(false); 
              setProfilePhoto(beUser?.profilePicUrl || "")
              }}>Cancel</Button>
            <Button
              type="primary"
              loading={isUpdating}
              onClick={() => form.submit()}
            >
              Save Changes
            </Button>
          </div>
        ) : (
          <Button type="primary" onClick={() => setIsEditing(true)}>
            Edit Profile
          </Button>
        )
      }
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleProfileSave}
        initialValues={{
          name:
            (beUser?.firstName || "") +
            " " +
            (beUser?.lastName || ""),
          jobRole: beUser?.role,
          phoneNumber: beUser?.phoneNumber,
          countryCode: beUser?.countryCode,
          email: beUser?.emailId,
        }}
        labelCol={{ flex: "200px" }}
        wrapperCol={{ flex: "1" }}
        labelAlign="left"
      >
        <Form.Item label="Name" name="name" className={styles.formItem}>
          {isEditing ? (
            <Input style={{ maxWidth: 320, height: 39 }} />
          ) : (
            <Text className={styles.readOnlyValue}>
              {beUser?.firstName}{" "}
              {beUser?.lastName}
            </Text>
          )}
        </Form.Item>

        <Form.Item label="Profile Photo" className={styles.formItem}>
          <div className={styles.avatarUploader}>
            <Avatar
              size={48}
              src={profilePhoto || undefined}
              icon={<User size={32} />}
            />
            {isEditing && (
              <Flex gap={8}>
                <Upload {...uploadProps}>
                  <Button style={{ height: 39 }} type="text" size="small" loading={isUploadingImage}>
                    Change
                  </Button>
                </Upload>
                {profilePhoto && (
                  <Button style={{ height: 39 }} type="text" size="small" danger onClick={handleDeleteImage}>
                    Remove
                  </Button>
                )}
              </Flex>
            )}
          </div>
        </Form.Item>

        <Form.Item label="Job Role" name="jobRole" className={styles.formItem}>
          {isEditing ? (
            <Input style={{ maxWidth: 320, height: 39 }} />
          ) : (
            <Text className={styles.readOnlyValue}>
              {beUser?.role || "Brand User"}
            </Text>
          )}
        </Form.Item>

        <Form.Item label="Access Level" className={styles.formItem}>
          <div className={styles.accessLevel}>
            <span className={styles.dot}></span>
            <TextWithTooltip text={permissionGroups || ""} limit={30} textClassName={styles.readOnlyValue} />
          </div>  
        </Form.Item>

        <Form.Item label="Email" name="email" className={styles.formItem}>
          <Text className={styles.readOnlyValue}>
            {beUser?.emailId}
          </Text>
        </Form.Item>

        <Form.Item
          label={
            <Flex gap={2} align="center">
              Contact Phone&nbsp;
              <Tooltip title="Used for account recovery and important notifications.">
                <HelpCircle size={14} className={styles.helpTooltip} />
              </Tooltip>
            </Flex>
          }
          className={styles.formItem}
        >
          {isEditing ? (
            <Input.Group compact>
              <Form.Item
                name="countryCode"
                noStyle
                rules={[
                  { required: true, message: "Country code is required" },
                ]}
              >
                <Select
                  className={styles.field}
                  style={{ width: "30%", height: 39 }}
                  placeholder="Country Code"
                >
                  {countryCodeOptions.map((option) => (
                    <AntdSelect.Option key={option.value} value={option.value}>
                      {option.label}
                    </AntdSelect.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                noStyle
                rules={[
                  { required: true, message: "Phone number is required" },
                  {
                    pattern: /^\d+$/,
                    message: "Enter digits only (no country code)",
                  },
                ]}
              >
                <Input
                  placeholder="Enter phone number"
                  className={styles.field}
                  style={{ width: "70%", height: 39 }}
                />
              </Form.Item>
            </Input.Group>
          ) : beUser?.phoneNumber ? (
            <Text className={styles.readOnlyValue}>
              {beUser?.countryCode}{" "}
              {beUser?.phoneNumber}
            </Text>
          ) : (
            <Button onClick={() => setIsEditing(true)} size="small">
              Add contact phone
            </Button>
          )}
        </Form.Item>

        <div className={styles.sectionDivider}>
          <Form.Item
            label="Password"
            className={styles.formItem}
            style={{ width: "fit-content" }}
          >
            <DisabledPopover
              isDisabled={disableTimer?.mins > 0 || disableTimer?.secs > 0}
              content={
                "Please check your email for password reset instructions. If you don't see it, make sure to check your spam or junk folder."
              }
            >
              <Button
                loading={isResetting}
                disabled={disableTimer?.mins > 0 || disableTimer?.secs > 0}
                onClick={() => passwordReset()}
              >
                Change Password
              </Button>
              {(disableTimer?.mins > 0 || disableTimer?.secs > 0) && (
                <p className={styles.warning}>
                  You'll be able to reset your password again in{" "}
                  {disableTimer?.mins < 10
                    ? "0" + disableTimer?.mins
                    : disableTimer?.mins}
                  :
                  {disableTimer?.secs < 10
                    ? "0" + disableTimer?.secs
                    : disableTimer?.secs}
                </p>
              )}
            </DisabledPopover>
          </Form.Item>
        </div>
      </Form>
      <Modal
        closable
        open={triggerCheckEmail}
        onCancel={() => setTriggerCheckEmail(false)}
        footer={null}
      >
        <MockCheckEmail
          email={beUser?.emailId}
          onCancel={() => setTriggerCheckEmail(false)}
        />
      </Modal>
    </Card>
  );
}
